/**
 * stellar_parts — everything stellar that is not a star's own disc (the natural object layer, 2026-10-07): what flows
 * between the stars of a pair, what falls onto a compact one, what a young or a dying star throws off.
 *
 * `star_view.ts` owns the stars (the sun rig, a companion body, the clock, the key light). A PART hangs on that scene:
 * it reads the tracked stars, moves with the same wall clock in `frame()`, is seated in `place()`, and may add a light
 * of its own to the key (`scene.lights`) or veil a star (`star.veil`). Parts are built by `buildStellarParts(scene)`
 * from the system's configuration and kinds.
 *
 * ONE PRIMITIVE: THE GLOWING VOLUME. Every part is an ellipsoid of thin, glowing gas — a real mesh at its TRUE place
 * in the star frame (the view's group), drawn additively. Its fragment integrates the gas along the eye's ray in
 * closed form (no march): a FILL (a disc, a jet, a lobe, a spiral — the chord through the ellipsoid × a profile at
 * its mid-plane crossing), a SHELL (the chord through a thin spherical layer: limb-brightened, a bubble) and a HALO
 * (the column of an r⁻² wind). A flattened ellipsoid is a disc that still has a picture edge-on; a long thin one is a
 * jet. One material, one compile; each mesh carries its numbers in `userData.sp` (THE PER-OBJECT LAW).
 *
 * NO DEPTH ORDER AT THE STAR. Gas is additive and cannot hide anything, and the star's disc is a quad at another
 * depth, so OCCLUSION IS ANALYTIC: each volume is told where up to two stars stand and keeps only the share of its
 * chord in FRONT of a star the ray meets (a disc passes behind its star; a donor eclipses its neighbour's disc).
 * Dust that should DIM a star does it through `star.veil` — additive gas cannot.
 *
 * THE FRAGMENT RAY. The chord is built from the fragment's own point on the shell (never the camera's quadratic,
 * which cancels in float32 a few hundred radii out); the camera enters only as a direction and for the case of an
 * eye inside the gas. Star tests use the perpendicular form |cross(S − eye, ray)|.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float, positionLocal, positionWorld, cameraPosition, normalize, length, dot, cross, max, min, clamp, mix,
  smoothstep, pow, exp, sqrt, floor, fract, sin, atan, step,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { setBodyNose } from '../space_sun.js';
import type { NaturalEnv, StarPlace } from './natural_api.js';
import type { SystemMakeup } from './natural_types.js';
import {
  DWARF_NOVA, NOVA, dwarfNovaLight, novaLight, novaShellM, lastEvent, pairAt, pairEclipse, starHash01, wander, STAR_LOOK,
  type LightPart, type PairSpec, type Rgb, type V3,
} from './star_look.js';
import type { TrackedStar } from './star_view.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** The stellar scene as a part sees it. */
export interface StellarScene {
  readonly env: NaturalEnv;
  readonly makeup: SystemMakeup;
  /** [0] the primary (the sun rig), [1] a close companion. Positions are sector metres about the barycentre. */
  readonly stars: TrackedStar[];
  readonly pair: PairSpec | null;
  /** The star frame: a child sits at sector metres about the barycentre (the view keeps it on the floating origin). */
  readonly group: THREE.Group;
  /** Lights a part adds to the ONE key light this frame (cleared before the parts' `frame()`). */
  readonly lights: LightPart[];
  /** The key scale those lights are worth on their own (a nova outshines its stars); a part raises it in `frame()`. */
  lightsBase: number;
  nowS: number;
}

export interface StellarPart {
  readonly name: string;
  /** Top of the sun update: advance with the wall clock. */
  frame?(dt: number, nowS: number): void;
  /** After the stars are seated: `right` / `up` / `fwd` are the camera's axes in the root's frame. */
  place(p: StarPlace, right: THREE.Vector3, up: THREE.Vector3, fwd: THREE.Vector3): void;
  state?(): Record<string, unknown>;
  dispose(): void;
}

// ── the glowing volume ──────────────────────────────────────────────────────────────────────────────────────────
/** A volume's numbers (read by the one material through `userData.sp`). Lengths are in the unit sphere's own units. */
export interface VolParams {
  /** × the fill, the shell, the halo; w = how much the fill is a spiral arm (0 = none). */
  mode: THREE.Vector4;
  /** Shell radius and half-width; the halo's core radius; the spiral's pitch (radii per turn). */
  shape: THREE.Vector4;
  /** Fill: radial power (brightness ∝ ρ^−p), inner cut-off radius, axial power (> 0 = a jet: bright at local y = −1), cone width (0 = none). */
  prof: THREE.Vector4;
  /** Texture: amplitude, radial and axial frequency (lattice cells per radius), angular cells per turn. */
  tex: THREE.Vector4;
  /** Clocks (reduced on the CPU): turn of the pattern (turns), outflow (lattice cells, mod 64), the rings' phase (turns), the hot spot's azimuth (rad). */
  anim: THREE.Vector4;
  /** Hot spot: radius, radial width, gain; w = the spiral arm's width (turns). */
  spot: THREE.Vector4;
  /** Opacity of the fill (0 = thin gas: brighter edge-on; 6 = a disc with a surface), ring amplitude, rings per radius, colour by height (1 = along the axis). */
  aux: THREE.Vector4;
  /** Inner colour (linear) and the gain; outer colour and the exponent of the blend between them. */
  colA: THREE.Vector4; colB: THREE.Vector4;
  /** The eye in the volume's own frame; w = a fade. */
  cam: THREE.Vector4;
  /** Metres per unit on each axis; w = the largest. */
  scl: THREE.Vector4;
  /** Up to two stars that can hide it: centre (render space) and radius (0 = none). */
  occ0: THREE.Vector4; occ1: THREE.Vector4;
}
const PARAM_KEYS = ['mode', 'shape', 'prof', 'tex', 'anim', 'spot', 'aux', 'colA', 'colB', 'cam', 'scl', 'occ0', 'occ1'] as const;
const ZERO4 = new THREE.Vector4();
/** The shell the fragments ride on: a little outside the unit sphere, so every ray through the sphere has a back face. */
const SHELL_R = 1.04;

let volGeo: THREE.SphereGeometry | null = null;
let volMat: THREE.MeshBasicNodeMaterial | null = null;

/** A lattice noise that repeats every 64 cells along `a` and every `perB` along `b` (a clock may wrap without a pop). */
const pmod = (x: N, y: N): N => x.sub(y.mul(floor(x.div(y))));
const hash2 = (a: N, b: N): N => fract(sin(a.mul(127.1).add(b.mul(311.7))).mul(43758.5453)); // sin/fract: sign-safe (three's hash() is not)
const lattice = (a: N, b: N, perB: N): N => {
  const ia = floor(a), ib = floor(b), fa = fract(a), fb = fract(b);
  const ua = fa.mul(fa).mul(float(3).sub(fa.mul(2))), ub = fb.mul(fb).mul(float(3).sub(fb.mul(2)));
  const a0 = pmod(ia, float(64)), a1 = pmod(ia.add(1), float(64)), b0 = pmod(ib, perB), b1 = pmod(ib.add(1), perB);
  return mix(mix(hash2(a0, b0), hash2(a1, b0), ua), mix(hash2(a0, b1), hash2(a1, b1), ua), ub);
};

function volumeMaterial(): THREE.MeshBasicNodeMaterial {
  if (volMat) return volMat;
  const U = {} as Record<(typeof PARAM_KEYS)[number], N>;
  for (const k of PARAM_KEYS) {
    U[k] = (uniform(new THREE.Vector4()) as N).onObjectUpdate(({ object }: { object: THREE.Object3D | null }) => (object?.userData?.sp as VolParams | undefined)?.[k] ?? ZERO4);
  }
  const color = Fn(() => {
    // every shared value is declared here, at the top (THE PLACEMENT LAW); nothing below branches
    const pf = vec3(positionLocal).toVar();
    const camL = vec3(U.cam.xyz).toVar();
    const d = normalize(pf.sub(camL)).toVar();
    const pd = dot(pf, d).toVar();
    const m = pf.sub(d.mul(pd)).toVar();                         // the ray's closest point to the centre
    const b2 = dot(m, m).toVar();
    const b = sqrt(b2).toVar();
    const meets = step(b2, float(1)).toVar();
    const h = sqrt(max(float(0), float(1).sub(b2))).toVar();     // half the chord
    const camMid = dot(m.sub(camL), d).toVar();                  // eye → mid-chord (negative: the eye is past it, inside)
    const seen = max(float(0), h.add(min(h, camMid))).toVar();   // the chord in front of the eye
    const sW = length(d.mul(U.scl.xyz)).toVar();                 // metres per unit along this ray
    const lenN = seen.mul(sW).div(max(U.scl.w, float(1))).toVar();
    const inFrac = clamp(seen.div(max(h.mul(2), float(1e-4))), 0, 1).toVar();
    // the world ray, for the stars
    const dw = vec3(positionWorld.sub(cameraPosition)).toVar();
    const Dpf = max(length(dw), float(1)).toVar();
    const dW = dw.div(Dpf).toVar();
    const tMid = Dpf.sub(pd.mul(sW)).toVar();
    const t1 = tMid.add(h.mul(sW)).toVar();
    const t0 = max(tMid.sub(h.mul(sW)), float(0)).toVar();
    const front = (o: N): N => {
      const sc = vec3(o.xyz.sub(cameraPosition));
      const ts = dot(sc, dW);
      const q = length(cross(sc, dW));
      const R = max(o.w, float(1e-3));
      const hit = float(1).sub(smoothstep(R.mul(0.95), R.mul(1.05), q)).mul(step(float(0.5), o.w)).mul(step(float(0), ts));
      const tHit = ts.sub(sqrt(max(float(0), R.mul(R).sub(q.mul(q)))));
      return mix(float(1), clamp(tHit.sub(t0).div(max(t1.sub(t0), float(1))), 0, 1), hit);
    };
    const vis = front(U.occ0).mul(front(U.occ1)).toVar();
    // where the ray crosses the mid-plane (a disc's pattern lives there), kept inside the chord
    // (the crossing fades to the mid-chord as the ray comes level with the plane, and only a FILL uses it: a plain
    //  −m.y ÷ d.y flips from one end of the chord to the other across the eye's own horizon — a seam through a shell)
    const cross1 = m.y.negate().mul(d.y).div(d.y.mul(d.y).add(0.0025)).mul(step(float(0.5), U.mode.x));
    const pm = m.add(d.mul(clamp(cross1, h.negate(), h))).toVar();
    const isAx = step(float(1e-3), U.prof.z).toVar();
    const rhoM = length(vec2(m.x, m.z)).toVar();
    const rho = mix(length(vec2(pm.x, pm.z)), rhoM, isAx).toVar();
    const turn = atan(pm.z, pm.x).div(6.283185307).toVar();      // −½ … ½
    const yN = clamp(float(0.5).sub(m.y.mul(0.5)), 1e-4, 1).toVar(); // 1 at the foot of a jet (local y = −1), 0 at its tip
    // texture: one lattice in (radius + height − outflow, angle + turn)
    const perB = max(U.tex.w, float(1));
    const n = lattice(rho.mul(U.tex.y).add(m.y.mul(U.tex.z)).sub(U.anim.y).add(64), turn.add(U.anim.x).add(4).mul(perB), perB).toVar();
    // (the lattice is cylindrical: on a shell or a halo it would pinch along the volume's own axis, so it fades out there)
    const texAmp = U.tex.x.mul(mix(smoothstep(0.05, 0.4, rho), float(1), step(float(0.5), U.mode.x)));
    const tex = max(float(0), float(1).add(texAmp.mul(n.mul(2).sub(1)))).toVar();
    // FILL
    const radial = pow(max(rho, float(0.05)), U.prof.x.negate()).mul(smoothstep(U.prof.y, U.prof.y.mul(1.35).add(1e-3), rho)).mul(float(1).sub(smoothstep(0.8, 1.0, rho)));
    const axial = pow(yN, U.prof.z);
    const coneW = max(U.prof.w, float(1e-4)).mul(float(1.15).add(m.y));
    const cone = mix(float(1), exp(rhoM.div(max(coneW, float(1e-4))).pow(2).negate()), step(float(1e-4), U.prof.w));
    const armD = fract(rho.div(max(U.shape.w, float(1e-3))).sub(turn).sub(U.anim.x).add(0.5)).sub(0.5);
    // pow2 (x·x), never pow(x, 2): these three bases are negative over half their range and pow of a negative base is undefined in GLSL / WGSL — a NaN there poisons every volume (they share this material), even one whose weight is 0
    const arm = mix(float(1), exp(armD.div(max(U.spot.w, float(1e-3))).pow2().negate()), U.mode.w);
    const spotT = fract(turn.sub(U.anim.w.div(6.283185307)).add(0.5)).sub(0.5);
    const hot = U.spot.z.mul(exp(rho.sub(U.spot.x).div(max(U.spot.y, float(1e-3))).pow2().negate())).mul(exp(spotT.div(0.045).pow2().negate()));
    const kap = max(U.aux.x, float(1e-3));
    const thick = float(1).sub(exp(kap.mul(lenN).negate())).div(kap);
    const fill = thick.mul(radial.mul(axial).mul(cone).mul(arm).mul(tex).add(hot));
    // SHELL: the chord through a thin layer
    const seg = (r: N): N => sqrt(max(float(0), r.mul(r).sub(b2)));
    const shell = seg(U.shape.x.add(U.shape.y)).sub(seg(max(U.shape.x.sub(U.shape.y), float(0)))).mul(2).mul(inFrac).mul(tex).mul(axial);
    // HALO: the column of an r⁻² wind, with rings (detached shells seen in projection)
    const be = sqrt(b2.add(U.shape.z.mul(U.shape.z))).toVar();
    const rings = float(1).add(U.aux.y.mul(sin(b.mul(U.aux.z).sub(U.anim.z).mul(6.283185307))));
    const halo = U.shape.z.mul(2).div(be).mul(atan(h.div(be))).mul(float(1).sub(smoothstep(0.75, 1.0, b))).mul(inFrac).mul(rings).mul(tex);
    const I = U.mode.x.mul(fill).add(U.mode.y.mul(shell)).add(U.mode.z.mul(halo)).mul(U.colA.w).mul(U.cam.w).mul(vis).mul(meets);
    const cr = clamp(mix(mix(b, rho, step(float(0.5), U.mode.x)), float(1).sub(yN), U.aux.w), 0, 1);
    const rgb = mix(vec3(U.colA.xyz), vec3(U.colB.xyz), pow(max(cr, float(1e-4)), max(U.colB.w, float(0.05))));
    return vec4(rgb.mul(max(I, float(0))), 1);
  });
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending, side: THREE.BackSide });
  mat.colorNode = color();
  mat.fog = false;
  mat.toneMapped = true;
  markMrtOverlay(mat);          // thin gas over whatever is behind it: it must not rewrite the surface targets
  mat.userData.shared = true;
  mat.name = 'stellar-volume';
  volMat = mat;
  return mat;
}

const Y_AXIS = new THREE.Vector3(0, 1, 0);
const finite = (v: number, fallback = 0): number => (Number.isFinite(v) ? v : fallback);

/** One glowing ellipsoid. `at` is its centre in the star frame; `axis` its local +Y. */
export class Vol {
  readonly mesh: THREE.Mesh;
  readonly sp: VolParams;
  readonly at = new THREE.Vector3();
  /** The stars that can hide it. */
  occ: [TrackedStar | null, TrackedStar | null] = [null, null];
  constructor(group: THREE.Group, name: string, order = -1) {
    volGeo ??= new THREE.SphereGeometry(SHELL_R, 40, 20);
    volGeo.userData.shared = true;
    const sp = {} as VolParams;
    for (const k of PARAM_KEYS) sp[k] = new THREE.Vector4();
    sp.colA.set(1, 1, 1, 0); sp.colB.set(1, 1, 1, 1); sp.cam.set(0, 0, 3, 1); sp.scl.set(1, 1, 1, 1); sp.shape.set(0.9, 0.03, 0.1, 1); sp.spot.set(0.9, 0.08, 0, 0.1);
    this.sp = sp;
    this.mesh = new THREE.Mesh(volGeo, volumeMaterial());
    this.mesh.name = `stellar:${name}`;
    this.mesh.userData.sp = sp;
    this.mesh.frustumCulled = false;   // a jet's sphere bound is useless, and the count is a handful
    this.mesh.renderOrder = order;
    group.add(this.mesh);
  }
  size(x: number, y: number, z: number): this {
    const sx = Math.max(1, finite(x, 1)), sy = Math.max(1, finite(y, 1)), sz = Math.max(1, finite(z, 1));
    this.mesh.scale.set(sx, sy, sz); this.sp.scl.set(sx, sy, sz, Math.max(sx, sy, sz));
    return this;
  }
  aim(axis: THREE.Vector3): this { if (axis.lengthSq() > 1e-12) this.mesh.quaternion.setFromUnitVectors(Y_AXIS, _n.copy(axis).normalize()); return this; }
  color(a: Rgb, b: Rgb, exponent = 1): this { this.sp.colA.x = a[0]; this.sp.colA.y = a[1]; this.sp.colA.z = a[2]; this.sp.colB.set(b[0], b[1], b[2], exponent); return this; }
  set gain(g: number) { this.sp.colA.w = Math.max(0, finite(g)); }
  get gain(): number { return this.sp.colA.w; }
  dispose(): void { this.mesh.removeFromParent(); }
}
const _n = new THREE.Vector3(), _c = new THREE.Vector3(), _s = new THREE.Vector3();

/** Every volume of a system and the steps that move them: one part, seated once a frame. */
class PartsRig implements StellarPart {
  readonly name = 'gas';
  readonly vols: Vol[] = [];
  readonly onFrame: Array<(dt: number, nowS: number) => void> = [];
  readonly onPlace: Array<(p: StarPlace, right: THREE.Vector3, up: THREE.Vector3) => void> = [];
  readonly info: Record<string, unknown> = {};
  constructor(readonly scene: StellarScene) {}
  vol(name: string, order = -1): Vol { const v = new Vol(this.scene.group, name, order); this.vols.push(v); return v; }
  frame(dt: number, nowS: number): void { for (const f of this.onFrame) f(dt, nowS); }
  place(p: StarPlace, right: THREE.Vector3, up: THREE.Vector3): void {
    for (const f of this.onPlace) f(p, right, up);
    const g = this.scene.group;
    g.updateWorldMatrix(true, false);
    p.cam.getWorldPosition(_c);
    for (const v of this.vols) {
      const mesh = v.mesh;
      mesh.visible = v.sp.colA.w > 1e-4 && v.sp.cam.w > 1e-4;
      if (!mesh.visible) continue;
      mesh.position.copy(v.at);
      mesh.updateMatrix();
      mesh.matrixWorld.multiplyMatrices(g.matrixWorld, mesh.matrix);
      _s.copy(_c).applyMatrix4(_inv.copy(mesh.matrixWorld).invert());
      if (!(Number.isFinite(_s.x) && Number.isFinite(_s.y) && Number.isFinite(_s.z))) { mesh.visible = false; continue; } // never a NaN in a uniform
      v.sp.cam.x = _s.x; v.sp.cam.y = _s.y; v.sp.cam.z = _s.z;
      for (let i = 0; i < 2; i++) {
        const s = v.occ[i], o = i === 0 ? v.sp.occ0 : v.sp.occ1;
        if (!s) { o.set(0, 0, 0, 0); continue; }
        _s.copy(s.abs).applyMatrix4(g.matrixWorld);
        o.set(_s.x, _s.y, _s.z, Math.max(0, finite(s.baseR * s.pulse.radius)));
      }
    }
  }
  state(): Record<string, unknown> { return { volumes: this.vols.filter((v) => v.mesh.visible).map((v) => v.mesh.name.slice(8)), ...this.info }; }
  dispose(): void { for (const v of this.vols) v.dispose(); this.vols.length = 0; }
}
const _inv = new THREE.Matrix4();

// ── what the gas is made of (true colour, linear) ───────────────────────────────────────────────────────────────
const HOT: Rgb = [0.62, 0.76, 1.0];       // a disc's inner edge, a white dwarf's glare (20,000 K and up)
const WARM: Rgb = [1.0, 0.62, 0.3];       // a disc's rim, a stream (5,000–6,000 K)
const EMBER: Rgb = [1.0, 0.34, 0.12];     // cool gas and warm dust (3,000 K)
const HALPHA: Rgb = [1.0, 0.25, 0.3];     // hydrogen's red line, as an eye or an RGB camera gets it (pink-crimson)
const PALE: Rgb = [1.0, 0.9, 0.78];

/** The normal of a pair's orbit (the way the orbit turns is counter-clockwise about it). */
function orbitNormal(p: PairSpec, out: THREE.Vector3): THREE.Vector3 {
  const a: V3 = [0, 0, 0], b: V3 = [0, 0, 0], b2: V3 = [0, 0, 0];
  pairAt(p, 0, a, b); pairAt(p, p.periodS * 0.2, a, b2);
  return out.set(b[1] * b2[2] - b[2] * b2[1], b[2] * b2[0] - b[0] * b2[2], b[0] * b2[1] - b[1] * b2[0]).normalize();
}

/** A lobe-filling star is a tear-drop: its outline reaches for its neighbour (as far as the disc quad has room for). */
function addNoses(rig: PartsRig, amounts: [number, number], sharp: number): void {
  const [a, b] = rig.scene.stars;
  if (!a || !b) return;
  rig.onPlace.push((_p, right, up) => {
    for (const [s, o, amt] of [[a, b, amounts[0]], [b, a, amounts[1]]] as Array<[TrackedStar, TrackedStar, number]>) {
      if (amt <= 0) continue;
      _n.subVectors(o.abs, s.abs).normalize();
      const x = _n.dot(right), y = _n.dot(up), l = Math.hypot(x, y);   // a nose aimed at the eye shows no point
      if (l > 1e-3) setNose(s, x / l, y / l, amt * l, sharp);
    }
  });
}
const setNose = (s: TrackedStar, x: number, y: number, amount: number, sharp: number): void => { if (Number.isFinite(x + y + amount)) setBodyNose(s.body, x, y, Math.min(0.14, amount), sharp); };

interface DiscOpts { inner: Rgb; outer: Rgb; thick: number; cut: number; gain: number; stream: boolean; flux: number; dwarfNova: boolean }
/**
 * An accretion disc round the companion, in the orbit's plane: hot and blue-white inside, cooler at its rim
 * (T ∝ r^−¾: a white dwarf's disc runs from ~30,000 K to ~6,000 K), with the STREAM from the donor's inner point
 * striking the rim ahead of the line of centres — the hot spot — and, on a white dwarf, DWARF-NOVA outbursts (EV-02):
 * every several minutes the whole disc flips to its hot state for a minute and a half and outshines both stars.
 */
function addAccretion(rig: PartsRig, o: DiscOpts): void {
  const sc = rig.scene, p = sc.pair!, [a, b] = sc.stars, seed = sc.makeup.primary.seed;
  const nrm = orbitNormal(p, new THREE.Vector3());
  const disc = rig.vol('disc');
  disc.occ = [a, b];
  disc.size(p.discM, p.discM * o.thick, p.discM).aim(nrm).color(o.inner, o.outer, 0.7);
  disc.sp.mode.set(1, 0, 0, 0);
  disc.sp.prof.set(1.15, o.cut, 0, 0);
  disc.sp.tex.set(0.55, 7, 0, 14);
  disc.sp.aux.set(6, 0, 0, 0);
  const stream = o.stream ? rig.vol('stream') : null;
  if (stream) { stream.occ = [a, b]; stream.color(PALE, WARM, 1); stream.sp.mode.set(1, 0, 0, 0); stream.sp.prof.set(0, 0, 0.35, 0); stream.sp.tex.set(0.4, 0, 5, 0); stream.sp.aux.set(0, 0, 0, 1); }
  const spin = 14 + starHash01(`${seed}:discspin`) * 6;          // the pattern's turn (s): the rim's orbit, to the eye
  const lead = 0.95;                                              // the hot spot leads the donor by ~55°
  const u = new THREE.Vector3(), hs = new THREE.Vector3(), q = new THREE.Quaternion(), qi = new THREE.Quaternion();
  let burst = 1;
  const discLight: LightPart = { rgb: [1, 1, 1], flux: 0, vis: 1 };
  const pa: V3 = [0, 0, 0], pb: V3 = [0, 0, 0];
  rig.onFrame.push((_dt, nowS) => {
    burst = o.dwarfNova ? dwarfNovaLight(lastEvent(seed, 'dn', nowS, DWARF_NOVA.everyS, 0.5).since) : 1;
    b.emission *= 0.4;                                            // an accretor is seen THROUGH its disc: the disc is the light, not a bare point's glare
    const hotness = (burst - 1) / (DWARF_NOVA.gain - 1);
    disc.gain = o.gain * (1 + 1.6 * hotness);
    disc.color(o.inner, [o.outer[0] + (o.inner[0] - o.outer[0]) * hotness * 0.8, o.outer[1] + (o.inner[1] - o.outer[1]) * hotness * 0.8, o.outer[2] + (o.inner[2] - o.outer[2]) * hotness * 0.8], 0.7);
    disc.sp.anim.x = (nowS / spin) % 1;
    disc.sp.spot.set(0.86, 0.09, stream ? 2.2 * (1 - 0.6 * hotness) : 0, 0.1);
    // the disc's light joins the key: the donor can eclipse it (the deep eclipses of a cataclysmic variable)
    pa[0] = a.abs.x; pa[1] = a.abs.y; pa[2] = a.abs.z; pb[0] = b.abs.x; pb[1] = b.abs.y; pb[2] = b.abs.z;
    const e = pairEclipse(sc.env.shipAbs(), pa, a.radiusM, pb, p.discM * 0.6);
    const starFlux = STAR_LOOK[a.kind].emission * a.baseR * a.baseR;
    const mixc = 0.35 + 0.65 * hotness;
    discLight.rgb = [o.outer[0] + (o.inner[0] - o.outer[0]) * mixc, o.outer[1] + (o.inner[1] - o.outer[1]) * mixc, o.outer[2] + (o.inner[2] - o.outer[2]) * mixc];
    // (an extra light counts by what of it is in view and is never part of the stars' own share: a hidden disc must not
    //  dim the key below the stars' light)
    discLight.flux = starFlux * o.flux * burst * e.visB; discLight.vis = 1;
    sc.lights.push(discLight);
    if (burst > 1.02) sc.lightsBase = Math.max(sc.lightsBase, STAR_LOOK[a.kind].light * (1 + 0.07 * (burst - 1) * e.visB));
    rig.info.disc = { burst: Math.round(burst * 100) / 100, vis: Math.round(e.visB * 1000) / 1000 };
  });
  rig.onPlace.push(() => {
    disc.at.copy(b.abs);
    u.subVectors(a.abs, b.abs); const sep = u.length() || 1; u.divideScalar(sep);
    hs.copy(u).applyQuaternion(q.setFromAxisAngle(nrm, lead));     // forward along the orbit from the donor's bearing
    const l = hs.clone().applyQuaternion(qi.copy(disc.mesh.quaternion).invert());
    disc.sp.anim.w = Math.atan2(l.z, l.x);
    if (stream) {
      const p1 = _n.copy(a.abs).addScaledVector(u, -a.baseR * 1.05), p2 = _s.copy(b.abs).addScaledVector(hs, p.discM * 0.9);
      const len = p1.distanceTo(p2);
      stream.at.copy(p1).add(p2).multiplyScalar(0.5);
      stream.aim(hs.subVectors(p2, p1)).size(sep * 0.035, len * 0.5, sep * 0.035);
      stream.gain = 5;
    }
  });
}

/**
 * A nova on a white dwarf (EV-01): hydrogen piled on the dwarf ignites — the point outshines the system in seconds,
 * fades over a minute, and a shell of gas leaves the pair (GK Persei's "firework": knots, ~1,000 km/s). The
 * schedule is the seed's and the wall clock's: every client sees the same nova. A symbiotic's recurs sooner.
 */
function addNova(rig: PartsRig, wd: TrackedStar, everyS: number, peak: number): void {
  const sc = rig.scene, seed = sc.makeup.primary.seed, F = sc.makeup.footprintM;
  const shell = rig.vol('nova-shell');
  shell.occ = [sc.stars[0] ?? null, sc.stars[1] ?? null];
  shell.sp.mode.set(0, 1, 0.25, 0);
  shell.sp.shape.set(0.9, 0.05, 0.3, 1);
  shell.sp.tex.set(0.75, 5, 5, 11);
  const light: LightPart = { rgb: [1, 0.92, 0.82], flux: 0, vis: 1 };
  let since = Infinity;
  rig.onFrame.push((_dt, nowS) => {
    since = lastEvent(seed, 'nova', nowS, everyS, 0.5).since;
    const L = novaLight(since, peak);
    if (L > 1.001) {
      wd.emission *= Math.min(1 + (L - 1) * 0.3, 14);              // the point itself: a glare for a minute
      light.flux = STAR_LOOK[sc.stars[0].kind].emission * sc.stars[0].baseR * sc.stars[0].baseR * 0.06 * L * wd.visShip; light.vis = 1;
      sc.lights.push(light);
      sc.lightsBase = Math.max(sc.lightsBase, STAR_LOOK[sc.stars[0].kind].light * (1 + 0.45 * Math.log10(L) * wd.visShip));
    }
    const u = since / NOVA.shellS;
    const on = since >= 0 && u < 1;
    shell.gain = on ? 0.9 * Math.pow(1 - u, 1.5) * Math.min(1, since / 3) : 0;
    if (on) {
      const r = novaShellM(since, F);
      shell.size(r, r, r);
      const k = Math.min(1, since / 90);                           // white-hot gas first, then hydrogen's red as it thins
      shell.color([PALE[0] + (HALPHA[0] - PALE[0]) * k, PALE[1] + (HALPHA[1] - PALE[1]) * k, PALE[2] + (HALPHA[2] - PALE[2]) * k], HALPHA, 1);
    }
    rig.info.nova = { since: Math.round(Math.min(since, 99999)), light: Math.round(L * 100) / 100 };
  });
  rig.onPlace.push(() => { shell.at.copy(wd.abs); });
}

// ── discs, jets and envelopes round ONE star (tier 3: compact objects and young stars) ──────────────────────────
const smooth01 = (x: number): number => { const u = Math.max(0, Math.min(1, x)); return u * u * (3 - 2 * u); };
/** An axis tipped `tilt` rad off the system's normal toward a seeded side (a disc in the system's plane is a line from the lanes). */
function tiltedAxis(seed: number, tag: string, tilt: number): THREE.Vector3 {
  const az = starHash01(`${seed}:${tag}:az`) * Math.PI * 2;
  return new THREE.Vector3(Math.sin(tilt) * Math.cos(az), Math.cos(tilt), Math.sin(tilt) * Math.sin(az)).normalize();
}
interface RingOpts {
  radiusM: number; thick: number; inner: Rgb; outer: Rgb; gain: number;
  /** Inner cut-off (× the radius), brightness ∝ ρ^−power, opacity (0 = thin gas), texture [amplitude, rings per radius, cells per turn]. */
  cut: number; power: number; opacity: number; tex: [number, number, number];
  /** Turn of the pattern (s; 0 = still) and outward drift (cells per s). */
  spinS?: number; drift?: number;
  /** Spiral arms: weight, pitch (radii per turn), width (turns). */
  spiral?: [number, number, number];
  /** Dust: the star dims to this when the eye is in the disc's plane (HH 30's dark lane), over this sine of elevation. */
  veil?: [number, number];
}
/** A disc (or a ring, or a torus) round a star, about `axis`. Returns the volume. */
function addRing(rig: PartsRig, name: string, star: TrackedStar, axis: THREE.Vector3, o: RingOpts): Vol {
  const v = rig.vol(name);
  v.occ = [rig.scene.stars[0] ?? null, rig.scene.stars[1] ?? null];
  v.size(o.radiusM, o.radiusM * o.thick, o.radiusM).aim(axis).color(o.inner, o.outer, 0.8);
  v.sp.mode.set(1, 0, 0, o.spiral ? o.spiral[0] : 0);
  v.sp.prof.set(o.power, o.cut, 0, 0);
  v.sp.tex.set(o.tex[0], o.tex[1], 0, o.tex[2]);
  v.sp.aux.set(o.opacity, 0, 0, 0);
  if (o.spiral) { v.sp.shape.w = o.spiral[1]; v.sp.spot.w = o.spiral[2]; }
  v.gain = o.gain;
  rig.onFrame.push((_dt, nowS) => {
    if (o.spinS) v.sp.anim.x = (nowS / o.spinS) % 1;
    if (o.drift) v.sp.anim.y = (nowS * o.drift) % 64;
    if (o.veil) {
      const e = rig.scene.env.shipAbs();
      _n.set(e.x - star.abs.x, e.y - star.abs.y, e.z - star.abs.z).normalize();
      star.veil *= o.veil[0] + (1 - o.veil[0]) * smooth01(Math.abs(_n.dot(axis)) / o.veil[1]);
    }
  });
  rig.onPlace.push(() => { v.at.copy(star.abs); });
  return v;
}
interface JetOpts { lengthM: number; widthM: number; base: Rgb; tip: Rgb; gain: number; cone: number; fade: number; knots: [number, number]; drift: number }
/** Two jets from a star along ± an axis (a function: a pulsar's beams turn). Knots drift outward on the wall clock. */
function addJets(rig: PartsRig, name: string, star: TrackedStar, axisAt: (nowS: number, out: THREE.Vector3) => THREE.Vector3, o: JetOpts, sides: Array<1 | -1> = [1, -1]): Vol[] {
  const ax = new THREE.Vector3();
  const vols = sides.map((sgn) => {
    const v = rig.vol(`${name}${sgn > 0 ? '+' : '-'}`);
    v.occ = [rig.scene.stars[0] ?? null, rig.scene.stars[1] ?? null];
    v.color(o.base, o.tip, 1);
    v.sp.mode.set(1, 0, 0, 0);
    v.sp.prof.set(0, 0, o.fade, o.cone);
    v.sp.tex.set(o.knots[0], 0, o.knots[1], 0);
    v.sp.aux.set(0, 0, 0, 1);
    v.size(o.widthM, o.lengthM * 0.5, o.widthM);
    v.gain = o.gain;
    return v;
  });
  rig.onFrame.push((_dt, nowS) => { for (const v of vols) v.sp.anim.y = (nowS * o.drift) % 64; });
  rig.onPlace.push(() => {
    axisAt(rig.scene.nowS, ax);
    vols.forEach((v, i) => { const sgn = sides[i]; _n.copy(ax).multiplyScalar(sgn); v.at.copy(star.abs).addScaledVector(_n, o.lengthM * 0.5); v.aim(_s.copy(_n)); });
  });
  return vols;
}
/** A wind or an envelope: an r⁻² halo round a star. */
function addHalo(rig: PartsRig, name: string, star: TrackedStar, radiusM: number, inner: Rgb, outer: Rgb, gain: number, core: number, tex: [number, number, number] = [0.3, 4, 9], rings: [number, number] = [0, 0]): Vol {
  const v = rig.vol(name);
  v.occ = [rig.scene.stars[0] ?? null, rig.scene.stars[1] ?? null];
  v.sp.mode.set(0, 0, 1, 0); v.sp.shape.set(0.9, 0.03, core, 1); v.sp.tex.set(tex[0], tex[1], tex[1], tex[2]); v.sp.aux.set(0, rings[0], rings[1], 0);
  v.color(inner, outer, 0.6).size(radiusM, radiusM, radiusM); v.gain = gain;
  rig.onPlace.push(() => { v.at.copy(star.abs); });
  return v;
}
const SYNC: Rgb = [0.66, 0.78, 1.0];      // synchrotron light: a featureless blue-white (the Crab's inner nebula)
const DUST: Rgb = [0.82, 0.7, 0.56];      // starlight off dust, a little reddened
const spinAxis = (star: TrackedStar) => (_t: number, out: THREE.Vector3): THREE.Vector3 => out.copy(star.axis);

/** What a single star (or either star of a pair) wears by its KIND. */
function addKindParts(rig: PartsRig, star: TrackedStar, alone: boolean): void {
  const sc = rig.scene, F = sc.makeup.footprintM, seed = star.seed, show = sc.makeup.showpiece, cfg = sc.makeup.config;
  switch (star.kind) {
    case 'pulsar': {
      // THE LIGHTHOUSE: two beams along the magnetic axis, which stands ~35° off the spin axis and turns with the star
      // (7–10 s a turn on the wall clock: slow enough to follow, and the KEY light never blinks with it)
      const tip = 0.6;
      addJets(rig, 'beam', star, (_t, out) => {
        const ph = star.pulse.spin, c = Math.cos(ph), s = Math.sin(ph);
        return out.copy(star.axis).multiplyScalar(Math.cos(tip)).addScaledVector(star.x0, Math.sin(tip) * c).addScaledVector(star.z0, Math.sin(tip) * s).normalize();
      }, { lengthM: F * 0.95, widthM: F * 0.11, base: [0.8, 0.88, 1], tip: SYNC, gain: 1.5, cone: 0.42, fade: 0.9, knots: [0, 0], drift: 0 });
      if (show === 'pulsar_nebula') {
        // THE WIND NEBULA (NE-16): a torus in the spin's equator whose wisps leave the inner ring at half the speed of
        // light, and two jets along the spin axis (the Crab and Vela in Chandra's pictures) — small: inside the footprint
        addRing(rig, 'torus', star, star.axis, { radiusM: F * 0.5, thick: 0.2, inner: [0.86, 0.92, 1], outer: SYNC, gain: 1.5, cut: 0.26, power: 0.9, opacity: 0, tex: [0.7, 9, 3], drift: 1.1 });
        addJets(rig, 'jet', star, spinAxis(star), { lengthM: F * 0.75, widthM: F * 0.07, base: [0.86, 0.92, 1], tip: SYNC, gain: 1.3, cone: 0.5, fade: 0.6, knots: [0.5, 4], drift: 0.9 });
      }
      if (show === 'pulsar_runaway') {
        // THE BOW SHOCK (NE-19): a pulsar outrunning its own wind ploughs the gas ahead into a thin shell that glows in
        // hydrogen's red (the Guitar Nebula's head), and leaves a trail. Thin and faint: it may pass the footprint.
        const az = starHash01(`${seed}:run`) * Math.PI * 2;
        const tail = new THREE.Vector3(-Math.cos(az), -0.12, -Math.sin(az)).normalize();
        const R = F * 0.5, L = R * 2.4;
        const bow = rig.vol('bow-shock'); bow.occ = [star, null];
        bow.sp.mode.set(0, 1, 0, 0); bow.sp.shape.set(0.95, 0.018, 0.2, 1); bow.sp.prof.set(0, 0, 2.6, 0); bow.sp.tex.set(0.4, 4, 5, 9);
        bow.color(PALE, HALPHA, 0.5).size(R, L, R).aim(tail); bow.gain = 1.1;
        const trail = rig.vol('trail'); trail.occ = [star, null];
        trail.sp.mode.set(1, 0, 0, 0); trail.sp.prof.set(0, 0, 0.8, 0.5); trail.sp.tex.set(0.5, 0, 5, 0); trail.sp.aux.set(0, 0, 0, 1);
        trail.color(SYNC, [0.5, 0.55, 0.9], 1).size(R * 0.3, L * 1.3, R * 0.3).aim(tail); trail.gain = 0.6;
        rig.onPlace.push(() => { bow.at.copy(star.abs).addScaledVector(tail, L - R * 0.4); trail.at.copy(star.abs).addScaledVector(tail, L * 1.3); });
      }
      break;
    }
    case 'be_star':
      // (alone: the same thin equatorial disc a Be star sheds in a pair — there the pair builds it)
      if (alone) addRing(rig, 'decretion', star, star.axis, { radiusM: Math.min(F * 1.15, star.baseR * 2.1), thick: 0.06, inner: [1, 0.86, 0.9], outer: HALPHA, gain: 4.5, cut: Math.min(0.8, 0.95 / 2.1), power: 1.3, opacity: 3, tex: [0.4, 6, 9], spinS: 40 });
      break;
    case 'white_dwarf':
      if (cfg === 'wd_debris' && alone) {
        // a planetary system's rubble, ground down inside the dwarf's tidal reach (G29-38): a flat ring of dust lit by
        // the dwarf, open at the centre where the dust boils away
        addRing(rig, 'debris', star, tiltedAxis(seed, 'debris', 0.5), { radiusM: F * 0.42, thick: 0.045, inner: [0.9, 0.8, 0.7], outer: [0.6, 0.64, 0.74], gain: 7, cut: 0.3, power: 0.7, opacity: 6, tex: [0.6, 11, 2], spinS: 22 });
      }
      break;
    case 'protostar': {
      // EMBEDDED: the star is a dim red glow inside its own cloud; light escapes along the two cavities its outflow has
      // cleared (L1527's hourglass: blue where the dust is thin, orange where it is thick), and a jet runs down each
      // with knots that travel (Herbig–Haro objects, NE-07: 200–300 km/s)
      const ax = tiltedAxis(seed, 'proto', 1.15);                  // the jet lies well off the pole: the hourglass is seen side-on from the lanes
      addHalo(rig, 'envelope', star, F * 1.2, [1, 0.36, 0.14], [0.55, 0.1, 0.03], 0.75, 0.22, [0.5, 4, 9]);
      addJets(rig, 'cavity', star, (_t, out) => out.copy(ax), { lengthM: F * 1.15, widthM: F * 0.5, base: [1, 0.72, 0.45], tip: [0.62, 0.74, 1], gain: 0.2, cone: 0.6, fade: 1.3, knots: [0.35, 3], drift: 0 });
      addJets(rig, 'jet', star, (_t, out) => out.copy(ax), { lengthM: F * 2.3, widthM: F * 0.07, base: [1, 0.7, 0.62], tip: HALPHA, gain: 1.6, cone: 0.5, fade: 0.35, knots: [0.9, 7], drift: 0.3 });
      addRing(rig, 'disc', star, ax, { radiusM: F * 0.8, thick: 0.16, inner: EMBER, outer: [0.5, 0.2, 0.08], gain: 3.5, cut: 0.3, power: 0.8, opacity: 5, tex: [0.4, 5, 1], veil: [0.22, 0.5] });
      rig.onFrame.push(() => { star.veil *= 0.55; });              // and from any side it is seen through its cloud
      break;
    }
    case 't_tauri':
    case 'herbig': {
      if (!alone) break;                                           // (a young PAIR's discs are cut short by the neighbour: built with the pair)
      // a flared disc of gas and dust, cleared by the star's magnetic field out to a few stellar radii where the rim
      // glows; rings and gaps where worlds are forming (HL Tauri). A T Tauri star still throws a small jet.
      const herbig = star.kind === 'herbig', ax = tiltedAxis(seed, 'disc', 0.5 + starHash01(`${seed}:dt`) * 0.3);
      const R = F * STELLAR_REACH_PART;
      addRing(rig, 'disc', star, ax, {
        radiusM: R, thick: 0.1, inner: herbig ? [1, 0.82, 0.62] : EMBER, outer: herbig ? [0.7, 0.8, 1] : DUST, gain: herbig ? 9 : 6,
        cut: Math.min(0.7, (star.baseR * 1.3) / R), power: 0.9, opacity: 5, tex: [0.55, 6, 1], spinS: 60,
        spiral: herbig ? [0.55, 0.42, 0.13] : undefined, veil: [0.3, 0.16],
      });
      if (!herbig) addJets(rig, 'jet', star, (_t, out) => out.copy(ax), { lengthM: F * 1.6, widthM: F * 0.07, base: [1, 0.7, 0.62], tip: HALPHA, gain: 1.3, cone: 0.5, fade: 0.5, knots: [0.8, 6], drift: 0.3 });
      break;
    }
    // ── tier 4: what a dying or a massive star throws off. Thin, faint, never solid: these may pass the footprint. ──
    case 'agb': case 'mira': case 'carbon_star': {
      // THE ENVELOPE (NE-11): a slow dusty wind (r⁻²) lit by the star, with the arcs of its uneven mass loss
      // (IRC+10216's nested shells, a few hundred years apart) drifting outward, and ONE thin detached shell from the
      // last thermal pulse (R Sculptoris, TT Cygni). A carbon star's is soot: redder, and it dims the star as it puffs.
      const carbon = star.kind === 'carbon_star';
      const env = addHalo(rig, 'envelope', star, F * 2.0, carbon ? [1, 0.36, 0.12] : [1, 0.62, 0.36], carbon ? [0.6, 0.12, 0.04] : [0.8, 0.32, 0.15], carbon ? 0.55 : 0.45, 0.3, [0.35, 4, 9], [0.4, 5]);
      const shell = rig.vol('detached-shell'); shell.occ = [star, null];
      shell.sp.mode.set(0, 1, 0, 0); shell.sp.shape.set(0.93, 0.02, 0.2, 1); shell.sp.tex.set(0.6, 6, 6, 13);
      shell.color(carbon ? [1, 0.4, 0.16] : [1, 0.66, 0.42], carbon ? [0.8, 0.2, 0.07] : [0.9, 0.4, 0.2], 1).size(F * 2.7, F * 2.7, F * 2.7); shell.gain = 0.5;
      rig.onFrame.push((_dt, nowS) => {
        env.sp.anim.z = (nowS / 80) % 1;
        env.gain = (carbon ? 0.55 : 0.45) * (0.6 + 0.4 * Math.min(1.4, star.pulse.light));   // the envelope is lit by the star: it follows a Mira's swing
        if (carbon) star.veil *= 0.72 + 0.28 * wander(seed, 'soot', nowS, 500);
      });
      rig.onPlace.push(() => { shell.at.copy(star.abs); });
      break;
    }
    case 'post_agb': {
      // THE PREPLANETARY NEBULA (NE-12): the star has thrown off its envelope and hides in a thick dusty waist; its
      // light escapes through the two holes as SEARCHLIGHT BEAMS across the old wind's concentric arcs (the Egg Nebula)
      const ax = tiltedAxis(seed, 'ppn', 1.2);
      const env = addHalo(rig, 'arcs', star, F * 2.1, [1, 0.92, 0.74], [0.7, 0.72, 0.85], 0.3, 0.28, [0.25, 4, 9], [0.28, 6]);
      addJets(rig, 'beam', star, (_t, out) => out.copy(ax), { lengthM: F * 2.2, widthM: F * 0.75, base: [1, 0.95, 0.8], tip: [0.72, 0.8, 1], gain: 1.5, cone: 0.42, fade: 0.9, knots: [0.45, 6], drift: 0 });
      addRing(rig, 'waist', star, ax, { radiusM: F * 0.95, thick: 0.3, inner: [0.7, 0.5, 0.32], outer: [0.4, 0.26, 0.16], gain: 1.6, cut: 0.35, power: 0.6, opacity: 5, tex: [0.4, 4, 5], veil: [0.3, 0.3] });
      rig.onFrame.push((_dt, nowS) => { env.sp.anim.z = (nowS / 120) % 1; });
      break;
    }
    case 'lbv': {
      // THE EJECTED SHELL: a giant eruption's two hollow lobes of dusty gas with a thin skirt at the waist (η Carinae's
      // Homunculus: mostly a reflection nebula — the star's own light off dust), lumpy, a little reddened
      const ax = tiltedAxis(seed, 'lbv', 1.0), L = F * 2.1, W = Math.max(F * 0.9, star.baseR * 1.25);   // (wider than the star itself, or they hide inside its disc)
      for (const sgn of [1, -1]) {
        const lobe = rig.vol(`lobe${sgn > 0 ? '+' : '-'}`); lobe.occ = [star, null];
        lobe.sp.mode.set(0.25, 1, 0, 0); lobe.sp.shape.set(0.9, 0.05, 0.2, 1); lobe.sp.prof.set(0, 0, 0, 0); lobe.sp.tex.set(0.6, 5, 5, 11);
        lobe.color([1, 0.86, 0.68], [0.95, 0.56, 0.32], 1).size(W, L * 0.5, W).aim(_n.copy(ax).multiplyScalar(sgn)); lobe.gain = 0.8;
        const off = ax.clone().multiplyScalar(sgn * L * 0.5);
        rig.onPlace.push(() => { lobe.at.copy(star.abs).add(off); });
      }
      addRing(rig, 'skirt', star, ax, { radiusM: F * 2.0, thick: 0.05, inner: [1, 0.8, 0.6], outer: [0.9, 0.5, 0.3], gain: 0.9, cut: 0.5, power: 0.8, opacity: 0, tex: [0.7, 3, 14] });
      break;
    }
    case 'wolf_rayet': case 'ob_star': {
      if (!alone) break;
      // THE WIND BUBBLE (NE-14): a 1,700–2,000 km/s wind sweeps the gas round the star into a thin shell — hydrogen's red
      // in its filaments, oxygen's blue-green as a skin (the Crescent, the Bubble Nebula), the star off its centre
      const wr = star.kind === 'wolf_rayet', R = F * (wr ? 2.3 : 2.7);
      const off = tiltedAxis(seed, 'bubble', 1.3).multiplyScalar(R * 0.28);
      const bub = rig.vol('wind-bubble'); bub.occ = [star, null];
      bub.sp.mode.set(0, 1, wr ? 0.12 : 0.05, 0); bub.sp.shape.set(0.93, wr ? 0.03 : 0.02, 0.5, 1); bub.sp.tex.set(wr ? 0.75 : 0.5, 5, 5, 13);
      bub.color(HALPHA, [0.36, 0.84, 0.8], 3).size(R, R * 0.9, R); bub.gain = wr ? 0.6 : 0.3;
      rig.onPlace.push(() => { bub.at.copy(star.abs).add(off); });
      break;
    }
    case 'red_supergiant':
      // plumes and arcs of dust, thrown off unevenly (Betelgeuse's plume to six stellar radii, VY Canis Majoris's knots)
      addHalo(rig, 'plumes', star, F * 2.0, [1, 0.56, 0.3], [0.7, 0.24, 0.1], 0.5, 0.34, [0.85, 3, 7]);
      break;
    case 'blue_supergiant':
      // a steady thin wind, scattering the star's own blue-white light (Rigel, Deneb)
      addHalo(rig, 'wind', star, F * 1.9, [0.78, 0.86, 1], [0.5, 0.62, 1], 0.2, 0.3, [0.15, 3, 7]);
      break;
    default: break;
  }
}
/** A young star's dust disc, in footprints. An infant star is drawn LARGE (0.5–0.9 of the footprint), so a disc held inside
 *  the reach (1.25) would be a sliver round its limb: the disc is thin, faint and not solid, and reaches past the reach
 *  as the brief allows for such things — the station (1.4 footprints + 50 km, in the system's plane) stands outside its
 *  tilted plane, the arrival point (2.4) beyond its rim. */
const STELLAR_REACH_PART = 2.0;

/** The parts a system's stars wear. */
export function buildStellarParts(scene: StellarScene): StellarPart[] {
  if (scene.env.flags.unlit) return [];
  const rig = new PartsRig(scene);
  const { pair, stars } = scene, cfg = scene.makeup.config, sc0 = scene;
  const [a, b] = stars;
  if (pair && a && b) {
    if (cfg === 'contact_binary') addNoses(rig, [0.12, 0.12], 2);
    if (cfg === 'semidetached') {
      addNoses(rig, [0.14, 0], 3);
      addAccretion(rig, { inner: HOT, outer: WARM, thick: 0.1, cut: 0.1, gain: 9, stream: true, flux: 0.3, dwarfNova: true });
      addNova(rig, b, NOVA.everyS, 60);
    }
    if (cfg === 'symbiotic') {
      // a red giant's slow wind fills the pair; the white dwarf lights a part of it (hydrogen's red) and wears a small disc
      const wind = rig.vol('wind'); wind.occ = [a, b];
      wind.sp.mode.set(0, 0, 1, 0); wind.sp.shape.set(0.9, 0.03, 0.22, 1); wind.sp.tex.set(0.35, 4, 4, 9);
      wind.color(EMBER, [0.9, 0.2, 0.08], 0.8); wind.gain = 0.2;
      const ion = rig.vol('ionised'); ion.occ = [a, b];
      ion.sp.mode.set(0, 0, 1, 0); ion.sp.shape.set(0.9, 0.03, 0.12, 1); ion.sp.tex.set(0.4, 5, 5, 9);
      ion.color(PALE, HALPHA, 0.5); ion.gain = 0.55;
      rig.onPlace.push(() => { wind.at.copy(a.abs); wind.size(pair.sepM * 2.3, pair.sepM * 2.3, pair.sepM * 2.3); ion.at.copy(b.abs); ion.size(pair.sepM * 0.5, pair.sepM * 0.5, pair.sepM * 0.5); });
      addAccretion(rig, { inner: HOT, outer: WARM, thick: 0.14, cut: 0.12, gain: 5, stream: false, flux: 0.05, dwarfNova: false });
      addNova(rig, b, NOVA.recurrentS, 40);
    }
    if (cfg === 'xray_binary') {
      // the donor feeds a disc round a neutron star or a black hole: white-hot inside, X-ray-lit outside, and two jets
      // straight out of it (a microquasar). A black hole is the dark middle of its own disc — never the sector's lens.
      const hole = b.kind === 'black_hole';
      const nrm = orbitNormal(pair, new THREE.Vector3());
      addNoses(rig, [0.14, 0], 3);
      addAccretion(rig, { inner: [0.72, 0.84, 1], outer: [1, 0.5, 0.22], thick: 0.13, cut: hole ? 0.16 : 0.06, gain: hole ? 10 : 8, stream: true, flux: 0.35, dwarfNova: false });
      addJets(rig, 'jet', b, (_t, out) => out.copy(nrm), { lengthM: scene.makeup.footprintM * (hole ? 0.85 : 0.6), widthM: scene.makeup.footprintM * 0.04, base: [0.86, 0.92, 1], tip: SYNC, gain: hole ? 1.8 : 1.1, cone: 0.5, fade: 0.7, knots: [0.7, 6], drift: 1.2 });
    }
    if (cfg === 'be_xray') {
      // a Be star spins so fast it sheds a thin disc from its equator (3–12 stellar radii, glowing in hydrogen's red);
      // the neutron star's long orbit dips into it at closest approach and flares (a "Type I" outburst, every orbit)
      const disc = addRing(rig, 'decretion', a, a.axis, { radiusM: pair.discM, thick: 0.06, inner: [1, 0.86, 0.9], outer: HALPHA, gain: 4.5, cut: Math.min(0.8, (a.baseR * 0.95) / pair.discM), power: 1.3, opacity: 3, tex: [0.4, 6, 9], spinS: 40 });
      const glow = addHalo(rig, 'outburst', b, pair.sepM * 0.16, [0.8, 0.9, 1], HOT, 0, 0.12);
      const light: LightPart = { rgb: HOT, flux: 0, vis: 1 };
      rig.onFrame.push((_dt, nowS) => {
        const ph = ((nowS / pair.periodS + pair.phase0) % 1 + 1.5) % 1 - 0.5;      // 0 at closest approach
        const out = Math.exp(-((ph / 0.05) ** 2));
        b.emission *= 1 + 5 * out; glow.gain = 2.2 * out;
        disc.gain = 4.5 * (1 + 0.25 * out);
        if (out > 0.02) { light.flux = STAR_LOOK[a.kind].emission * a.baseR * a.baseR * 0.25 * out * b.visShip; sc0.lights.push(light); }
        rig.info.periastron = Math.round(out * 100) / 100;
      });
    }
    if (cfg === 'young_binary') {
      // two infant stars: each keeps a disc cut short by the other's pull (a third of the way between them), and what
      // neither can hold circles BOTH in a wide ring (GG Tauri)
      const nrm = orbitNormal(pair, new THREE.Vector3());
      for (const s of [a, b]) addRing(rig, s === a ? 'disc-a' : 'disc-b', s, nrm, { radiusM: pair.discM, thick: 0.12, inner: EMBER, outer: DUST, gain: 7, cut: Math.min(0.8, (s.baseR * 1.08) / pair.discM), power: 0.9, opacity: 5, tex: [0.4, 5, 1], spinS: 50 });
      const ring = rig.vol('circumbinary'); ring.occ = [a, b];
      const R = pair.sepM * 1.9;
      ring.size(R, R * 0.08, R).aim(nrm).color(DUST, [0.6, 0.45, 0.35], 1); ring.sp.mode.set(1, 0, 0, 0); ring.sp.prof.set(0.6, 0.62, 0, 0); ring.sp.tex.set(0.5, 7, 0, 5); ring.sp.aux.set(4, 0, 0, 0); ring.gain = 3.2;
    }
  }
  if (pair && a && b && cfg === 'colliding_wind') {
    // THE PINWHEEL (SY-19): where the Wolf–Rayet star's wind meets its hot companion's, carbon dust condenses and is
    // carried off; the orbit winds it into an Archimedean spiral that turns once an orbit (WR 104: 241.5 days) and
    // rolls outward like a lawn sprinkler's jet. Thin and faint: it runs well past the footprint.
    const nrm = orbitNormal(pair, new THREE.Vector3()), R = scene.makeup.footprintM * 3.0, pitch = 0.3;
    const pin = rig.vol('pinwheel'); pin.occ = [a, b];
    pin.size(R, R * 0.05, R).aim(_n.copy(nrm).negate()).color([1, 0.78, 0.46], [0.8, 0.3, 0.12], 0.7);  // (aimed backwards: the arm TRAILS the orbit)
    pin.sp.mode.set(1, 0, 0, 1); pin.sp.shape.w = pitch; pin.sp.spot.w = 0.1;
    pin.sp.prof.set(0.9, 0.05, 0, 0); pin.sp.tex.set(0.55, 9, 0, 11); pin.sp.aux.set(0, 0, 0, 0); pin.gain = 2.6;
    const qi = new THREE.Quaternion(), l = new THREE.Vector3();
    rig.onPlace.push(() => {
      pin.at.set(0, 0, 0);
      // the arm leaves from the companion's side of the pair: the spiral is tied to the star, not to a clock of its own
      l.copy(b.abs).applyQuaternion(qi.copy(pin.mesh.quaternion).invert());
      const turnB = Math.atan2(l.z, l.x) / (Math.PI * 2), rho0 = b.abs.length() / R;
      pin.sp.anim.x = (((rho0 / pitch - turnB) % 1) + 1) % 1;
    });
  }
  for (const s of stars) addKindParts(rig, s, !pair);
  return rig.vols.length || rig.onPlace.length || rig.onFrame.length ? [rig] : [];
}
